import { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { compatApi } from "../../utils/Intercepter";
import useTokenStore from "../../stores/tokens";
import { BsCheck, BsX } from 'react-icons/bs';
import { toast } from "react-toastify";
import { downloadFile, loadImageWithAuth, revokeImageUrl } from "../../utils/ImageUtil";
import { PURPLE, MINT, hexToRgba } from "../../utils/designUtils";
import TransferMasterModal from "../../components/TransferMasterModal";
import GradeBadge from "../../components/GradeBadge";

const calcMergedDays = (ranges) => {
  const valid = ranges
    .filter(r => r.start)
    .map(r => ({ s: dayjs(r.start).startOf('day').valueOf(), e: dayjs(r.end || dayjs()).startOf('day').valueOf() }))
    .filter(r => r.e >= r.s)
    .sort((a, b) => a.s - b.s);
  if (!valid.length) return 0;
  const merged = [{ ...valid[0] }];
  for (let i = 1; i < valid.length; i++) {
    const last = merged[merged.length - 1];
    if (valid[i].s <= last.e) last.e = Math.max(last.e, valid[i].e);
    else merged.push({ ...valid[i] });
  }
  return merged.reduce((sum, r) => sum + Math.round((r.e - r.s) / 86400000) + 1, 0);
};

const ViewResumePage = () => {
  const { user } = useTokenStore();
  const currentUser = useLocation().state;
  const navigation = useNavigate();

  const initialUserInfoData = {
    userEmail: '', userName: '', userNameEn: '', organizationCode: '', organizationName: '',
    joinDate: '', leaveDate: '', contact: '', emergencyContact: '',
    negotiationDate: '', negotiationState: '', negotiatedSalary: '',
    contactStartDate: '', contactEndDate: '', department: '', position: '',
    gender: '', address: '', addressDetail: '', birthDate: '', expiredDate: '',
    eduGrade: '', techGrade: '',
    careers: [], certificates: [], projects: [], educations: [],
    itTrainings: [], specialSkills: [], preProjects: [], userDocuments: []
  };

  const fetchData = async () => {
    try {
      const requestBody = { userEmail: currentUser.userEmail, organizationCode: currentUser.organizationCode };
      const response = await compatApi.post('/user/userResumeInfo', requestBody);
      const data = response.data;
      if (data) {
        if (data.certificates) {
          data.certificates.sort((a, b) => {
            const nameA = (a.certificateName || "").replace(/\s+/g, "");
            const nameB = (b.certificateName || "").replace(/\s+/g, "");
            const isAChungbo = nameA.includes("정보처리기사");
            const isBChungbo = nameB.includes("정보처리기사");
            if (isAChungbo && !isBChungbo) return -1;
            if (!isAChungbo && isBChungbo) return 1;
            const dateA = a.acquisitionDate ? dayjs(a.acquisitionDate) : dayjs(0);
            const dateB = b.acquisitionDate ? dayjs(b.acquisitionDate) : dayjs(0);
            return dateB.isAfter(dateA) ? 1 : -1;
          });
        }
        if (data.projects) data.projects.sort((a, b) => { const dA = a.startDate ? dayjs(a.startDate) : dayjs('0001-01-01'); const dB = b.startDate ? dayjs(b.startDate) : dayjs('0001-01-01'); return dB.isAfter(dA) ? 1 : -1; });
        if (data.preProjects) data.preProjects.sort((a, b) => { const dA = a.startDate ? dayjs(a.startDate) : dayjs('0001-01-01'); const dB = b.startDate ? dayjs(b.startDate) : dayjs('0001-01-01'); return dB.isAfter(dA) ? 1 : -1; });
        if (data.careers) data.careers.sort((a, b) => { const dA = a.startDate ? dayjs(a.startDate) : dayjs('0001-01-01'); const dB = b.startDate ? dayjs(b.startDate) : dayjs('0001-01-01'); return dB.isAfter(dA) ? 1 : -1; });
        setPersonalInfo(data);
      }
    } catch (error) {
      console.log(error);
      alert('오류가 발생했습니다.');
    }
  };

  useEffect(() => { fetchData(); }, [currentUser.userEmail, currentUser.organizationCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const [personalInfo, setPersonalInfo] = useState(initialUserInfoData);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingDepartment, setIsEditingDepartment] = useState(false);
  const [isSalaryAuthenticated, setIsSalaryAuthenticated] = useState(false);
  const [isEditingSalary, setIsEditingSalary] = useState(false);
  const [isEditingNegotiationDate, setIsEditingSalaryDate] = useState(false);
  const [isEditingJoinDate, setIsEditingJoinDate] = useState(false);
  const [isEditingExpireDate, setIsEditingExpireDate] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [certPurposeOption, setCertPurposeOption] = useState('관공서 제출용');
  const [certPurposeCustom, setCertPurposeCustom] = useState('');
  const [positionOptions, setPositionOptions] = useState([]); // 조직 직급 목록(서열순)
  const [departmentOptions, setDepartmentOptions] = useState([]); // 조직 부서 목록

  useEffect(() => {
    const orgCode = currentUser?.organizationCode;
    if (!orgCode) return;
    (async () => {
      try {
        const res = await compatApi.get(`/org/positions/${orgCode}`);
        if (res.ok) setPositionOptions((res.data || []).map(p => p.positionName));
      } catch { /* 실패 시 드롭다운 비움 — 자유입력 대체는 아래 select에서 처리 */ }
    })();
    (async () => {
      try {
        const res = await compatApi.get(`/org/departments/${orgCode}`);
        if (res.ok) setDepartmentOptions((res.data || []).map(d => d.departmentName));
      } catch { /* 실패 시 드롭다운 비움 — 자유입력 대체는 아래 select에서 처리 */ }
    })();
  }, [currentUser?.organizationCode]);
  const [beforeInput, setBeforeInput] = useState({});
  const [salaryBeforeInput, setSalaryBeforeInput] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordModalMode, setPasswordModalMode] = useState('view');
  const [password, setPassword] = useState('');
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);

  const handleDepartmentAndPositionUpdate = async () => {
    try {
      const requestBody = { userEmail: currentUser.userEmail, organizationCode: currentUser.organizationCode, department: personalInfo.department, position: personalInfo.position };
      const response = await compatApi.post('/org/updateDepartmentAndPosition', requestBody);
      if (response.ok) toast.success('인사 정보가 업데이트되었습니다.');
      else toast.error('인사 정보 업데이트에 실패했습니다.');
    } catch (error) { toast.error('인사 정보 업데이트 중 오류가 발생했습니다.'); }
  };

  const handleJoinDateUpdate = async () => {
    if (beforeInput.joinDate === personalInfo.joinDate) { setIsEditingJoinDate(false); return; }
    try {
      const requestBody = { userEmail: currentUser.userEmail, organizationCode: currentUser.organizationCode, joinDate: personalInfo.joinDate };
      const response = await compatApi.post('/org/updateJoinDate', requestBody);
      if (response.ok) toast.success('인사 정보가 업데이트되었습니다.');
      else toast.error('인사 정보 업데이트에 실패했습니다.');
    } catch (error) { toast.error('인사 정보 업데이트 중 오류가 발생했습니다.'); }
  };

  const handleSalaryPasswordSubmit = async () => {
    if (passwordModalMode === 'view') {
      try {
        const requestBody = { targetEmail: currentUser.userEmail, organizationCode: currentUser.organizationCode, secretKey: password };
        const response = await compatApi.post('/user/verifySalaryPassword', requestBody);
        if (response.ok) {
          const salaryData = response.data;
          setPersonalInfo({ ...personalInfo, negotiatedSalary: salaryData.negotiatedSalary, contactStartDate: salaryData.contactStartDate, contactEndDate: salaryData.contactEndDate, negotiationDate: salaryData.negotiationDate, negotiationState: salaryData.negotiationState });
          setIsSalaryAuthenticated(true); setShowPasswordModal(false); setPassword('');
          toast.success('연봉 정보를 조회했습니다.');
        } else { toast.error('암호를 다시 확인해주세요.'); }
      } catch (error) { toast.error('암호를 다시 확인해주세요.'); }
    } else if (passwordModalMode === 'update') {
      try {
        const requestBody = { userEmail: currentUser.userEmail, organizationCode: currentUser.organizationCode, negotiatedSalary: personalInfo.negotiatedSalary, contactStartDate: dayjs(personalInfo.contactStartDate).format('YYYY-MM-DD'), contactEndDate: dayjs(personalInfo.contactEndDate).format('YYYY-MM-DD'), secretKey: password };
        const response = await compatApi.post('/user/updateSalaryInfo', requestBody);
        if (response.ok) { toast.success('연봉 정보가 성공적으로 업데이트되었습니다.'); setIsEditingSalary(false); setShowPasswordModal(false); setPassword(''); setIsSalaryAuthenticated(false); }
        else toast.error('연봉 정보 업데이트에 실패했습니다.');
      } catch (error) { toast.error('연봉 정보 업데이트 중 오류가 발생했습니다.'); }
    }
  };

  const updateNegotiationDate = async () => {
    try {
      const requestBody = { userEmail: currentUser.userEmail, organizationCode: currentUser.organizationCode, negotiationDate: personalInfo.negotiationDate };
      const response = await compatApi.post('/org/updateSalaryNegotiationDate', requestBody);
      if (response.ok) { toast.success('연봉협상일이 업데이트되었습니다.'); setIsEditingSalaryDate(false); }
      else toast.error('연봉협상일 업데이트에 실패했습니다.');
    } catch (error) { toast.error('연봉협상일 업데이트 중 오류가 발생했습니다.'); }
  };

  const handleSalaryUpdateClick = () => { setPasswordModalMode('update'); setShowPasswordModal(true); };

  // 반환값: 편집 UI를 닫아도 되는지 여부. 검증 실패·요청 실패 시에는 false를 반환해
  // 호출부가 편집 모드를 유지하도록 한다(입력값을 잃지 않고 바로 고쳐 재시도할 수 있게).
  const handleExpireDateUpdate = async () => {
    if (beforeInput.leaveDate === personalInfo.leaveDate
      && beforeInput.leaveType === personalInfo.leaveType
      && beforeInput.leaveReason === personalInfo.leaveReason) { return true; }
    // 퇴사일 없이 유형/사유만 입력하면 서버에서는 "퇴사(예정) 취소"로 해석되어 아무 것도 저장되지 않는다.
    // leaveDate가 비어 있는데 유형·사유 중 하나라도 채워졌다면 입력 실수로 보고 여기서 막는다.
    if (!personalInfo.leaveDate && (personalInfo.leaveType || personalInfo.leaveReason)) {
      toast.error('퇴사일을 먼저 입력해주세요. 날짜 없이는 유형·사유가 저장되지 않습니다.');
      return false;
    }
    try {
      const requestBody = {
        leaveDate: personalInfo.leaveDate,
        leaveType: personalInfo.leaveType || null,
        leaveReason: personalInfo.leaveReason || null,
        organizationCode: currentUser.organizationCode,
        userEmail: currentUser.userEmail,
      };
      const response = await compatApi.post('/org/updateLeaveDate', requestBody);
      if (response.ok) {
        // 미래 날짜면 즉시 퇴사가 아니라 "퇴사 예정" 등록 — 확정은 당일 서버 배치가 처리
        const isScheduled = personalInfo.leaveDate && dayjs(personalInfo.leaveDate).isAfter(dayjs(), 'day');
        toast.success(isScheduled ? '퇴사 예정일이 등록되었습니다. 해당 일자에 퇴사 처리됩니다.' : '퇴사 처리가 완료되었습니다.');
        return true;
      }
      toast.error('퇴사 처리에 실패했습니다.');
      return false;
    } catch (error) {
      // 400: 활성 MASTER 퇴사 차단 등 정책 안내 메시지를 그대로 노출 (MASTER 위임/조직 삭제 유도)
      const msg = error?.response?.data;
      if (error?.response?.status === 400 && typeof msg === 'string' && msg) {
        toast.error(msg);
      } else {
        toast.error('퇴사 처리 중 오류가 발생했습니다.');
      }
      return false;
    }
  };

  const calculateFullCareer = () => {
    const careerList = personalInfo.careers || [];
    const isJoinDateMissing = !personalInfo.joinDate;
    const allRanges = [...careerList.map(c => ({ start: c.startDate, end: c.endDate })), ...(isJoinDateMissing ? [] : [{ start: personalInfo.joinDate, end: null }])];
    const totalDays = calcMergedDays(allRanges);
    const prevOnlyDays = calcMergedDays(careerList.map(c => ({ start: c.startDate, end: c.endDate })));
    const currentOrgDays = isJoinDateMissing ? 0 : calcMergedDays([{ start: personalInfo.joinDate, end: null }]);
    const convertDaysToText = (days) => {
      if (days <= 0) return "0개월";
      const years = Math.floor(days / 365); const months = Math.floor((days % 365) / (365 / 12));
      const yearStr = years > 0 ? `${years}년 ` : ""; const monthStr = months > 0 ? `${months}개월` : (years === 0 ? "0개월" : "");
      const totalMonthCount = (years * 12) + months; const totalMonthStr = totalMonthCount > 0 ? `(총 ${totalMonthCount}개월)` : "";
      return `${yearStr}${monthStr} ${totalMonthStr}`.trim();
    };
    const totalCareerText = convertDaysToText(totalDays);
    const prevCareerText = prevOnlyDays > 0 ? convertDaysToText(prevOnlyDays) : null;
    const currentCareerText = isJoinDateMissing ? "입사일 없음" : convertDaysToText(currentOrgDays);
    return (
      <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ background: `linear-gradient(135deg,${hexToRgba(PURPLE, .16)},${hexToRgba(MINT, .16)})`, color: PURPLE, fontWeight: 700, fontSize: 13, padding: '5px 12px', borderRadius: 8 }}>총 경력 {totalCareerText}</span>
        {prevCareerText && <span style={{ fontSize: 12, color: '#7a7989' }}>이전 경력 {prevCareerText}</span>}
        {prevCareerText && <span style={{ color: '#d4d3df' }}>·</span>}
        {isJoinDateMissing
          ? <strong style={{ color: '#ef4444', fontSize: 12 }}>{currentCareerText}</strong>
          : <span style={{ fontSize: 12, color: '#7a7989' }}>현재 {currentCareerText}</span>}
      </span>
    );
  };

  const handleImageDownload = async () => {
    if (!personalInfo.imageUrl) { toast.error("다운로드할 사진이 없습니다."); return; }
    const fileName = `${personalInfo.userName}_profile.png`;
    const success = await downloadFile(personalInfo.userEmail, fileName, '/my-info/download/profile');
    if (success) toast.success('프로필 사진 다운로드가 완료되었습니다.');
    else toast.error('파일 다운로드에 실패했습니다.');
  };

  const loadProfilePreview = async (userEmail) => {
    if (!userEmail) return;
    try {
      const url = await loadImageWithAuth(userEmail, '/my-info/download/profile');
      if (url) { if (profilePreviewUrl) revokeImageUrl(profilePreviewUrl); setProfilePreviewUrl(url); }
    } catch (error) { console.error("프로필 미리보기 로드 실패:", error); }
  };

  useEffect(() => {
    if (personalInfo.userEmail) loadProfilePreview(personalInfo.userEmail);
    return () => { if (profilePreviewUrl) revokeImageUrl(profilePreviewUrl); };
  }, [personalInfo.userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  // 조직 매니저(MASTER/MANAGER)가 조직원의 학력/자격증 증빙 파일을 다운로드.
  // 본인 파일이 아니므로 self-only 엔드포인트(/my-info/download) 대신 member 전용 엔드포인트를 사용한다.
  const handleFileDownload = async (type, id, fileName) => {
    try {
      const success = await downloadFile(id, fileName, `/my-info/member/download/${type}`, {
        userEmail: currentUser.userEmail,
        organizationCode: currentUser.organizationCode,
      });
      if (!success) toast.error("파일 다운로드에 실패했습니다.");
    }
    catch (error) { toast.error("파일 다운로드에 실패했습니다."); }
  };

  const handleDocumentUpload = async (e) => {
    const files = e.target.files;
    if (!files) return;
    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('files', file));
    formData.append('userEmail', currentUser.userEmail);
    formData.append('organizationCode', currentUser.organizationCode);
    try {
      const response = await compatApi.upload('/my-info/document/upload', formData);
      if (response.ok) { toast.success("문서가 업로드되었습니다."); await fetchData(); }
    } catch (error) { toast.error("업로드에 실패했습니다."); }
  };

  const handleDeleteDocument = async (id, fileName) => {
    if (!window.confirm(`${fileName} 문서를 삭제하시겠습니까?`)) return;
    try {
      const response = await compatApi.del(`/my-info/document/${id}`);
      if (response.ok) { toast.success("삭제되었습니다."); await fetchData(); }
    } catch (error) { toast.error("삭제 중 오류가 발생했습니다."); }
  };

  // 등급(학력 기준/기술 기준)은 더 이상 이 화면에서 계산하지 않는다.
  // 서버(GradeUtils/MyInfoService.syncAllOrganizationsGrades)가 유일하게 계산·저장하며,
  // 이 화면은 personalInfo.eduGrade / personalInfo.techGrade를 그대로 표시만 한다.

  const handleToggleCareerProfile = async (cert) => {
    const nextValue = !(cert.includeInCareerProfile !== false);
    const updated = { ...cert, includeInCareerProfile: nextValue };
    setPersonalInfo(prev => ({ ...prev, certificates: prev.certificates.map(c => c.id === cert.id ? updated : c) }));
    try {
      // 타인(조직원) 자격증의 경력프로필 노출 토글 — 매니저 전용 엔드포인트(MASTER/MANAGER 권한 검증)
      const response = await compatApi.put('/user/member-certificate/career-profile', {
        userEmail: currentUser.userEmail,
        organizationCode: currentUser.organizationCode,
        id: cert.id,
        includeInCareerProfile: nextValue,
      });
      if (!response.ok) { alert('수정에 실패했습니다.'); setPersonalInfo(prev => ({ ...prev, certificates: prev.certificates.map(c => c.id === cert.id ? cert : c) })); }
    } catch (error) { alert('오류가 발생했습니다.'); setPersonalInfo(prev => ({ ...prev, certificates: prev.certificates.map(c => c.id === cert.id ? cert : c) })); }
  };

  const handleProfileDownload = async () => {
    try {
      const requestData = {
        ...personalInfo,
        careerList: personalInfo.careers || [], educationList: personalInfo.educations || [],
        itTrainingList: personalInfo.itTrainings || [], specialSkillList: personalInfo.specialSkills || [],
        certificateList: (personalInfo.certificates || []).filter(c => c.includeInCareerProfile !== false),
        projectList: (personalInfo.projects || []).map(p => ({ ...p, assignments: p.assignments || p.role })),
        preProjectList: personalInfo.preProjects || [], documentList: personalInfo.userDocuments || []
      };
      const response = await compatApi.postFile('/my-info/download/profile-doc', requestData, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `경력프로필_${personalInfo.userName || '미기입'}.docx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("경력프로필 다운로드가 완료되었습니다.");
    } catch (error) { toast.error("경력프로필 생성 중 오류가 발생했습니다."); }
  };

  // 재직증명서 다운로드 — 퇴사자는 버튼 자체를 숨기므로(경력프로필로 충분) 여기 도달하는 건 항상 재직자다.
  // 관공서 제출용 공식 문서라 userEmail/organizationCode만 보내고, 회사명·대표자·재직정보는 서버가 직접 조회한다.
  // 용도(관공서/은행/기타)는 다운로드 직전 모달에서 선택받아 함께 보낸다.
  const handleCareerCertificateDownload = async () => {
    const purpose = certPurposeOption === '기타' ? certPurposeCustom.trim() : certPurposeOption;
    if (certPurposeOption === '기타' && !purpose) { toast.error('용도를 입력해주세요.'); return; }
    try {
      const requestData = { userEmail: personalInfo.userEmail, organizationCode: personalInfo.organizationCode, purpose };
      const response = await compatApi.postFile('/my-info/download/career-certificate-doc', requestData, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `재직증명서_${personalInfo.userName || '미기입'}.docx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("재직증명서 다운로드가 완료되었습니다.");
      setCertModalOpen(false);
    } catch (error) { toast.error("재직증명서 생성 중 오류가 발생했습니다."); }
  };

  // ── style helpers ──────────────────────────────────────────────────────────
  const cardS = { background: 'white', borderRadius: 16, border: '1px solid #f0eff5', boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.06)', overflow: 'hidden' };
  const b = (v, sm) => {
    const h = sm ? 28 : 34; const p = sm ? '0 8px' : '0 14px';
    const base = { height: h, padding: p, borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: sm ? 12 : 13, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, whiteSpace: 'nowrap' };
    if (v === 'primary') return { ...base, background: PURPLE, color: 'white' };
    if (v === 'danger') return { ...base, background: '#ef4444', color: 'white' };
    if (v === 'success') return { ...base, background: '#22c55e', color: 'white' };
    if (v === 'outline-primary') return { ...base, background: 'white', color: PURPLE, border: `1px solid ${PURPLE}` };
    if (v === 'outline-secondary') return { ...base, background: 'white', color: '#6b7280', border: '1px solid #d1d5db' };
    return base;
  };
  const inp = { height: 34, borderRadius: 6, border: '1.5px solid #ececf2', background: '#fafaff', padding: '0 10px', fontSize: 13, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', boxShadow: 'none', maxWidth: 'none', margin: 0 };
  const tblS = { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 };
  const tdS = { padding: '14px 14px', borderBottom: '1px solid #f4f3f9', fontSize: 12.5, color: '#3a3849', verticalAlign: 'middle', textAlign: 'left' };
  const thS = { padding: '11px 14px', fontSize: 11.5, fontWeight: 600, color: '#5d5b6e', borderBottom: '1px solid #ececf2', whiteSpace: 'nowrap', letterSpacing: '.02em', background: '#f7f6fb', textAlign: 'center' };

  const sHead = (title, count, action, badgeColor) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: 'linear-gradient(180deg,#faf9fd 0%,#f5f4fa 100%)', borderBottom: '1px solid #ececf2' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 4, height: 16, borderRadius: 2, background: `linear-gradient(180deg,${PURPLE},${MINT})`, flexShrink: 0, display: 'inline-block' }} />
        <span style={{ fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>{title}</span>
        {count != null && <span style={{ fontSize: 11, fontWeight: 600, background: hexToRgba(badgeColor || PURPLE, .12), color: badgeColor || PURPLE, padding: '2px 8px', borderRadius: 999, lineHeight: 1.45 }}>{count}</span>}
      </div>
      {action}
    </div>
  );

  const emptyRow = (colSpan, msg) => (
    <tr><td colSpan={colSpan}>
      <div style={{ padding: '44px 20px', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: hexToRgba(PURPLE, .08), color: PURPLE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <div>{msg}</div>
      </div>
    </td></tr>
  );

  const pencilBtn = (onClick) => (
    <button title="수정" onClick={onClick} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid #ececf2', background: 'white', color: '#8b8a99', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 140ms' }}
      onMouseEnter={(e) => { e.currentTarget.style.color = PURPLE; e.currentTarget.style.borderColor = PURPLE; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = '#8b8a99'; e.currentTarget.style.borderColor = '#ececf2'; }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    </button>
  );

  const fieldRow = (label, value, onEdit) => (
    <div style={{ display: 'grid', gridTemplateColumns: onEdit ? '108px 1fr auto' : '108px 1fr', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
      <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{value || '-'}</div>
      {onEdit && pencilBtn(onEdit)}
    </div>
  );

  const fileChip = (label, onClick) => (
    <button onClick={onClick} style={{ height: 26, padding: '0 10px', borderRadius: 6, background: hexToRgba(PURPLE, .08), border: 'none', color: PURPLE, fontSize: 11.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
      {label}
    </button>
  );

  // 퇴사 확정 또는 퇴사일 도래 — [[resignation-model]]의 isEffectivelyResigned와 동일한 기준.
  // 미래 날짜(퇴사 예정)는 아직 재직 중이므로 제외한다.
  const isResignedEmployee = !!personalInfo.resigned
    || (!!personalInfo.leaveDate && !dayjs(personalInfo.leaveDate).isAfter(dayjs(), 'day'));

  return (
    <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
      <div style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

        {/* 페이지 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
              <span>조직</span>
              <span style={{ fontSize: 10 }}>›</span>
              <span style={{ color: '#3a3849', fontWeight: 500 }}>이력서 조회</span>
            </div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>{personalInfo.userName || '이력서 조회'}</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>{personalInfo.organizationName} · {personalInfo.userEmail}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* 퇴사자는 재직증명서 대상이 아니므로(경력프로필로 충분) 버튼 자체를 숨긴다 */}
            {!isResignedEmployee && (
              <button onClick={() => { setCertPurposeOption('관공서 제출용'); setCertPurposeCustom(''); setCertModalOpen(true); }} style={{ height: 38, padding: '0 18px', borderRadius: 10, background: 'white', border: `1.5px solid ${hexToRgba(PURPLE, .3)}`, color: PURPLE, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="15" x2="15" y2="15" /><line x1="9" y1="11" x2="15" y2="11" />
                </svg>
                재직증명서 다운로드
              </button>
            )}
            <button onClick={handleProfileDownload} style={{ height: 38, padding: '0 18px', borderRadius: 10, background: `linear-gradient(135deg,${PURPLE},${MINT})`, border: 'none', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, .55)}`, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              경력프로필 다운로드
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* 기본 정보 */}
          <div style={cardS}>
            {sHead('기본 정보', null, null)}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 28, padding: '24px 24px 12px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: 160, height: 160, borderRadius: 18, background: `linear-gradient(135deg,${hexToRgba(PURPLE, .14)},${hexToRgba(MINT, .14)})`, border: `1.5px dashed ${hexToRgba(PURPLE, .3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, overflow: 'hidden' }}>
                  {profilePreviewUrl
                    ? <img src={profilePreviewUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <div style={{ width: 70, height: 70, borderRadius: '50%', background: `linear-gradient(135deg,${PURPLE},${MINT})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 22, boxShadow: `0 10px 24px -8px ${hexToRgba(PURPLE, .5)}` }}>{personalInfo.userName?.charAt(0) || '?'}</div>}
                </div>
                <button style={{ height: 30, padding: '0 12px', borderRadius: 7, background: 'white', border: '1.5px solid #ececf2', color: '#5d5b6e', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }} onClick={handleImageDownload}>사진 다운로드</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 32 }}>
                {fieldRow('성명', personalInfo.userName)}
                {fieldRow('영어 이름', personalInfo.userNameEn)}
                <div style={{ gridColumn: '1 / -1' }}>
                  {fieldRow('이메일', personalInfo.userEmail)}
                </div>
                {fieldRow('연락처', personalInfo.contact)}
                {fieldRow('긴급연락처', personalInfo.emergencyContact)}
                {fieldRow('생년월일', personalInfo.birthDate ? dayjs(personalInfo.birthDate).format('YYYY-MM-DD') : null)}
                {fieldRow('성별', personalInfo.gender)}
                <div style={{ gridColumn: '1 / -1' }}>
                  {fieldRow('주소', personalInfo.address ? `${personalInfo.address}${personalInfo.addressDetail ? ' ' + personalInfo.addressDetail : ''}` : null)}
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  {fieldRow('권한', personalInfo.userRole)}
                </div>
                <div style={{ gridColumn: '1 / -1', padding: '12px 0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr', gap: 12, alignItems: 'center' }}>
                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>나의 문서</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      {personalInfo.userDocuments?.map((doc, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', background: hexToRgba(PURPLE, .08), borderRadius: 6, padding: '4px 10px', gap: 6 }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: PURPLE, flexShrink: 0 }}><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                          <span style={{ cursor: 'pointer', fontSize: 12, fontWeight: 500, color: PURPLE, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={() => handleFileDownload('document', doc.id, doc.fileName)} title={doc.fileName}>{doc.fileName}</span>
                          <button onClick={() => handleDeleteDocument(doc.id, doc.fileName)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa9b7', padding: 0, display: 'flex', alignItems: 'center', fontSize: 15, lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#aaa9b7'}>×</button>
                        </div>
                      ))}
                      <label htmlFor="admin-doc-upload" style={{ height: 32, padding: '0 14px', borderRadius: 8, background: '#2a2841', border: 'none', color: 'white', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5 }}>+ 문서 추가</label>
                      <input id="admin-doc-upload" type="file" multiple hidden onChange={handleDocumentUpload} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 인사 정보 */}
          <div style={cardS}>
            {sHead('인사 정보', null, null)}
            <div style={{ padding: '16px 20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 32 }}>
                {/* 부서 */}
                {isEditingDepartment ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>부서</div>
                    <select style={inp} value={personalInfo.department || ''} onChange={(e) => { setBeforeInput({ ...beforeInput, department: personalInfo.department }); setPersonalInfo({ ...personalInfo, department: e.target.value }); }}>
                      <option value="">선택 안 함</option>
                      {departmentOptions.map(d => <option key={d} value={d}>{d}</option>)}
                      {personalInfo.department && !departmentOptions.includes(personalInfo.department) && (
                        <option value={personalInfo.department}>{personalInfo.department} (미등록)</option>
                      )}
                    </select>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button style={b('primary', true)} onClick={() => { handleDepartmentAndPositionUpdate(); setIsEditingDepartment(false); }}><BsCheck /></button>
                      <button style={b('danger', true)} onClick={() => { setPersonalInfo({ ...personalInfo, department: beforeInput.department }); setIsEditingDepartment(false); }}><BsX /></button>
                    </div>
                  </div>
                ) : fieldRow('부서', personalInfo.department, () => setIsEditingDepartment(true))}

                {/* 직책 */}
                {isEditing ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>직책</div>
                    <select style={inp} value={personalInfo.position || ''} onChange={(e) => { setBeforeInput({ ...beforeInput, position: personalInfo.position }); setPersonalInfo({ ...personalInfo, position: e.target.value }); }}>
                      <option value="">선택 안 함</option>
                      {positionOptions.map(p => <option key={p} value={p}>{p}</option>)}
                      {personalInfo.position && !positionOptions.includes(personalInfo.position) && (
                        <option value={personalInfo.position}>{personalInfo.position} (미등록)</option>
                      )}
                    </select>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button style={b('primary', true)} onClick={() => { handleDepartmentAndPositionUpdate(); setIsEditing(false); }}><BsCheck /></button>
                      <button style={b('danger', true)} onClick={() => { setPersonalInfo({ ...personalInfo, position: beforeInput.position }); setIsEditing(false); }}><BsX /></button>
                    </div>
                  </div>
                ) : fieldRow('직책', personalInfo.position, () => setIsEditing(true))}

                {/* MASTER 전용: 연봉협상일, 연봉 */}
                {user.currentRole === 'MASTER' && (<>
                  {isEditingNegotiationDate ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                      <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>연봉협상일</div>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <input type="number" min="1" max="12" style={{ ...inp, width: 60 }} placeholder="월" value={personalInfo.negotiationDate ? parseInt(personalInfo.negotiationDate.split('-')[0]) : ''} onChange={(e) => { const month = String(e.target.value).padStart(2, '0'); const day = personalInfo.negotiationDate ? personalInfo.negotiationDate.split('-')[1] : '01'; setPersonalInfo({ ...personalInfo, negotiationDate: `${month}-${day}` }); }} />
                        <span style={{ fontSize: 12 }}>월</span>
                        <input type="number" min="1" max="31" style={{ ...inp, width: 60 }} placeholder="일" value={personalInfo.negotiationDate ? parseInt(personalInfo.negotiationDate.split('-')[1]) : ''} onChange={(e) => { const day = String(e.target.value).padStart(2, '0'); const month = personalInfo.negotiationDate ? personalInfo.negotiationDate.split('-')[0] : '01'; setPersonalInfo({ ...personalInfo, negotiationDate: `${month}-${day}` }); }} />
                        <span style={{ fontSize: 12 }}>일</span>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button style={b('primary', true)} onClick={updateNegotiationDate}><BsCheck /></button>
                        <button style={b('danger', true)} onClick={() => { setPersonalInfo({ ...personalInfo, ...salaryBeforeInput }); setIsEditingSalaryDate(false); }}><BsX /></button>
                      </div>
                    </div>
                  ) : fieldRow('연봉협상일', personalInfo.negotiationDate ? (() => { const [m, d] = personalInfo.negotiationDate.split('-'); return `${parseInt(m)}월 ${parseInt(d)}일`; })() : null, () => setIsEditingSalaryDate(true))}

                  <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>연봉</div>
                    {isEditingSalary ? (
                      <>
                        <input type="number" style={inp} value={personalInfo.negotiatedSalary || ''} onChange={(e) => setPersonalInfo({ ...personalInfo, negotiatedSalary: e.target.value })} />
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button style={b('success', true)} onClick={handleSalaryUpdateClick}><BsCheck /></button>
                          <button style={b('danger', true)} onClick={() => { setPersonalInfo({ ...personalInfo, ...salaryBeforeInput }); setIsEditingSalary(false); }}><BsX /></button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{personalInfo.negotiatedSalary === null ? '-' : isSalaryAuthenticated ? `${parseInt(personalInfo.negotiatedSalary).toLocaleString()}원` : '암호화 되어 있습니다.'}</div>
                        {pencilBtn(() => {
                          if (personalInfo.negotiatedSalary === null) { setSalaryBeforeInput({ negotiatedSalary: null, negotiationDate: personalInfo.negotiationDate }); setIsEditingSalary(true); }
                          else if (!isSalaryAuthenticated) { setPasswordModalMode('view'); setShowPasswordModal(true); }
                          else { setSalaryBeforeInput({ negotiatedSalary: personalInfo.negotiatedSalary, negotiationDate: personalInfo.negotiationDate }); setIsEditingSalary(true); }
                        })}
                      </>
                    )}
                  </div>
                </>)}

                {/* 입사일 */}
                {isEditingJoinDate ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>입사일</div>
                    <input type="date" style={inp} value={personalInfo.joinDate ? dayjs(personalInfo.joinDate).format('YYYY-MM-DD') : ''} onChange={(e) => setPersonalInfo({ ...personalInfo, joinDate: e.target.value })} />
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button style={b('primary', true)} onClick={() => { handleJoinDateUpdate(); setIsEditingJoinDate(false); }}><BsCheck /></button>
                      <button style={b('danger', true)} onClick={() => { setPersonalInfo({ ...personalInfo, joinDate: beforeInput.joinDate }); setIsEditingJoinDate(false); }}><BsX /></button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>입사일</div>
                    <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>{personalInfo.joinDate ? dayjs(personalInfo.joinDate).format('YYYY-MM-DD') : '-'}</div>
                    {(currentUser.userEmail === user.userEmail || user.currentRole === 'MASTER') && !personalInfo.leaveDate &&
                      pencilBtn(() => { setBeforeInput({ ...beforeInput, joinDate: personalInfo.joinDate }); setIsEditingJoinDate(true); })}
                  </div>
                )}

                {/* 퇴사일 */}
                {isEditingExpireDate ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>퇴사일</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input type="date" style={inp} value={personalInfo.leaveDate ? dayjs(personalInfo.leaveDate).format('YYYY-MM-DD') : ''} onChange={(e) => setPersonalInfo({ ...personalInfo, leaveDate: e.target.value })} />
                      {/* 날짜 미입력 시 유형·사유는 저장되지 않으므로(서버가 취소로 해석) 입력 자체를 막아 입력 순서를 유도 */}
                      <select style={{ ...inp, opacity: personalInfo.leaveDate ? 1 : 0.5 }} disabled={!personalInfo.leaveDate} value={personalInfo.leaveType || ''} onChange={(e) => setPersonalInfo({ ...personalInfo, leaveType: e.target.value })}>
                        <option value="">{personalInfo.leaveDate ? '퇴사 유형 선택' : '퇴사일을 먼저 입력하세요'}</option>
                        <option value="자진퇴사">자진퇴사</option>
                        <option value="권고사직">권고사직</option>
                        <option value="계약만료">계약만료</option>
                        <option value="정년퇴직">정년퇴직</option>
                        <option value="기타">기타</option>
                      </select>
                      <input type="text" style={{ ...inp, opacity: personalInfo.leaveDate ? 1 : 0.5 }} disabled={!personalInfo.leaveDate} maxLength={500} placeholder="사유 (선택)" value={personalInfo.leaveReason || ''} onChange={(e) => setPersonalInfo({ ...personalInfo, leaveReason: e.target.value })} />
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button style={b('primary', true)} onClick={async () => { if (await handleExpireDateUpdate()) setIsEditingExpireDate(false); }}><BsCheck /></button>
                      <button style={b('danger', true)} onClick={() => { setPersonalInfo({ ...personalInfo, leaveDate: beforeInput.leaveDate, leaveType: beforeInput.leaveType, leaveReason: beforeInput.leaveReason }); setIsEditingExpireDate(false); }}><BsX /></button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>퇴사일</div>
                    <div style={{ fontSize: 13.5, color: '#1f1d2e', fontWeight: 500 }}>
                      {personalInfo.leaveDate ? dayjs(personalInfo.leaveDate).format('YYYY년 MM월 DD일') : '-'}
                      {personalInfo.leaveDate && personalInfo.leaveType && (
                        <span style={{ marginLeft: 6, fontSize: 11.5, fontWeight: 600, color: '#c44569', background: hexToRgba('#c44569', .08), padding: '2px 8px', borderRadius: 999 }}>{personalInfo.leaveType}</span>
                      )}
                      {personalInfo.leaveDate && personalInfo.leaveReason && (
                        <div style={{ fontSize: 12, color: '#8b8a99', fontWeight: 400, marginTop: 2 }}>{personalInfo.leaveReason}</div>
                      )}
                    </div>
                    {user.currentRole === 'MASTER' && !personalInfo.leaveDate && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        {/* MASTER 본인 상세에서만: 퇴사 전 다른 구성원에게 MASTER 위임 */}
                        {currentUser.userRole === 'MASTER' && (
                          <button
                            style={{ height: 28, fontSize: 12, padding: '0 10px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, color: PURPLE, background: hexToRgba(PURPLE, .08), border: `1px solid ${hexToRgba(PURPLE, .25)}` }}
                            onClick={() => setTransferModalOpen(true)}
                          >MASTER 위임</button>
                        )}
                        <button style={{ ...b('danger', true), height: 28, fontSize: 12 }} onClick={() => { setBeforeInput({ ...beforeInput, leaveDate: personalInfo.leaveDate, leaveType: personalInfo.leaveType, leaveReason: personalInfo.leaveReason }); setIsEditingExpireDate(true); }}>퇴사</button>
                      </div>
                    )}
                  </div>
                )}

                {/* 경력 - full width */}
                <div style={{ gridColumn: '1 / -1', padding: '12px 0', borderBottom: '1px solid #f4f3f9' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr', gap: 12, alignItems: 'start' }}>
                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500, paddingTop: 2 }}>경력</div>
                    <div>{calculateFullCareer()}</div>
                  </div>
                </div>

                {/* 등급 - 학력 기준 / 기술 기준 (서버가 계산·저장한 값을 그대로 표시, 수동 수정 없음, 한 줄에 함께 표시) */}
                {/* 상위 2단 그리드(columnGap: 32)와 동일한 구조를 그대로 복제해야 "기술등급" 라벨이 다른 우측 항목과 같은 위치에서 시작한다 */}
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 32, padding: '12px 0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr', gap: 12, alignItems: 'center' }}>
                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>학력등급</div>
                    <div><GradeBadge grade={personalInfo.eduGrade} /></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '108px 1fr', gap: 12, alignItems: 'center' }}>
                    <div style={{ fontSize: 12.5, color: '#8b8a99', fontWeight: 500 }}>기술등급</div>
                    <div><GradeBadge grade={personalInfo.techGrade} /></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 자격증 */}
          <div style={cardS}>
            {sHead('자격증', personalInfo.certificates?.length, null)}
            <div style={{ overflowX: 'auto' }}>
              <table style={tblS}>
                <thead>
                  <tr style={{ background: '#f7f6fb' }}>
                    <th style={{ ...thS, textAlign: 'left' }}>자격증명 <span style={{ color: '#aaa9b7', fontWeight: 400, fontSize: 11 }}>(☑ = 경력프로필 포함)</span></th>
                    <th style={thS}>취득일</th>
                    <th style={thS}>증빙서류</th>
                  </tr>
                </thead>
                <tbody>
                  {personalInfo.certificates?.length > 0 ? personalInfo.certificates.map((cert, i) => (
                    <tr key={i}>
                      <td style={tdS}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="checkbox" style={{ accentColor: PURPLE, cursor: 'pointer' }} checked={cert.includeInCareerProfile !== false} onChange={() => handleToggleCareerProfile(cert)} />
                          <span style={{ fontWeight: 600, color: '#1f1d2e' }}>{cert.certificateName || '-'}</span>
                        </div>
                      </td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{cert.acquisitionDate ? dayjs(cert.acquisitionDate).format('YYYY-MM-DD') : '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{cert.fileName ? fileChip('증빙서류', () => handleFileDownload('certificate', cert.id, cert.fileName)) : <span style={{ fontSize: 12, color: '#aaa9b7' }}>파일 없음</span>}</td>
                    </tr>
                  )) : emptyRow(3, '등록된 자격증 정보가 없습니다.')}
                </tbody>
              </table>
            </div>
          </div>

          {/* 학력 */}
          <div style={cardS}>
            {sHead('학력', personalInfo.educations?.length, null)}
            <div style={{ overflowX: 'auto' }}>
              <table style={tblS}>
                <thead>
                  <tr style={{ background: '#f7f6fb' }}>
                    <th style={{ ...thS, textAlign: 'left' }}>학교명</th>
                    <th style={thS}>학위</th>
                    <th style={thS}>기간</th>
                    <th style={{ ...thS, textAlign: 'left' }}>전공</th>
                    <th style={thS}>증명서</th>
                  </tr>
                </thead>
                <tbody>
                  {personalInfo.educations?.length > 0 ? personalInfo.educations.map((ed, i) => (
                    <tr key={i}>
                      <td style={{ ...tdS, fontWeight: 600, color: '#1f1d2e' }}>{ed.schoolName || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{ed.degree || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }}>{ed.startDate ? dayjs(ed.startDate).format('YYYY-MM-DD') : '-'} ~ {ed.endDate ? dayjs(ed.endDate).format('YYYY-MM-DD') : '-'}</td>
                      <td style={tdS}>{ed.major || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{ed.fileName ? fileChip('파일 보기', () => handleFileDownload('education', ed.id, ed.fileName)) : <span style={{ fontSize: 12, color: '#aaa9b7' }}>파일 없음</span>}</td>
                    </tr>
                  )) : emptyRow(5, '학력 정보가 없습니다.')}
                </tbody>
              </table>
            </div>
          </div>

          {/* 프로젝트 */}
          <div style={cardS}>
            {sHead('프로젝트', personalInfo.projects?.length)}
            <div style={{ overflowX: 'auto' }}>
              <table style={tblS}>
                <thead>
                  <tr style={{ background: '#f7f6fb' }}>
                    <th style={{ ...thS, textAlign: 'left', width: '18%' }}>프로젝트명</th>
                    <th style={{ ...thS, width: 160 }}>수행기간</th>
                    <th style={thS}>고객사</th>
                    <th style={thS}>역할</th>
                    <th style={{ ...thS, textAlign: 'left' }}>담당업무</th>
                    <th style={{ ...thS, textAlign: 'left', width: '18%' }}>기술스택</th>
                    <th style={{ ...thS, width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {personalInfo.projects?.length > 0 ? personalInfo.projects.map((project, i) => (
                    <tr key={i}>
                      <td style={{ ...tdS, fontWeight: 600, color: '#1f1d2e' }}>{project.projectName || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }}>{project.startDate} ~ {project.endDate || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{project.client || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{project.role || '-'}</td>
                      <td style={tdS}>{project.assignments || '-'}</td>
                      <td style={{ ...tdS, fontSize: 12 }}>{[project.os, project.dbInfo, project.language].filter(Boolean).join(' / ') || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>
                        <button style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #ececf2', background: 'white', color: '#5d5b6e', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => navigation('/project/edit', { state: { projectId: project.projectId } })}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                        </button>
                      </td>
                    </tr>
                  )) : emptyRow(7, '프로젝트 정보가 없습니다.')}
                </tbody>
              </table>
            </div>
          </div>

          {/* 이전 프로젝트 */}
          <div style={cardS}>
            {sHead('이전 프로젝트', personalInfo.preProjects?.length, null)}
            <div style={{ overflowX: 'auto' }}>
              <table style={tblS}>
                <thead>
                  <tr style={{ background: '#f7f6fb' }}>
                    <th style={{ ...thS, textAlign: 'left', width: '15%' }}>프로젝트명</th>
                    <th style={{ ...thS, width: 160 }}>수행기간</th>
                    <th style={thS}>고객사</th>
                    <th style={thS}>근무처</th>
                    <th style={thS}>역할</th>
                    <th style={{ ...thS, textAlign: 'left' }}>담당업무</th>
                    <th style={{ ...thS, textAlign: 'left', width: '15%' }}>기술스택</th>
                  </tr>
                </thead>
                <tbody>
                  {personalInfo.preProjects?.length > 0 ? personalInfo.preProjects.map((p, i) => (
                    <tr key={i}>
                      <td style={{ ...tdS, fontWeight: 600, color: '#1f1d2e' }}>{p.projectName || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }}>{p.startDate} ~ {p.endDate || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{p.client || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{p.companyName || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{p.role || '-'}</td>
                      <td style={tdS}>{p.content || '-'}</td>
                      <td style={{ ...tdS, fontSize: 12 }}>{[p.os, p.dbInfo, p.language].filter(Boolean).join(' / ') || '-'}</td>
                    </tr>
                  )) : emptyRow(7, '이전 프로젝트 정보가 없습니다.')}
                </tbody>
              </table>
            </div>
          </div>

          {/* 이전 근무처 */}
          <div style={cardS}>
            {sHead('이전 근무처', personalInfo.careers?.length, null)}
            <div style={{ overflowX: 'auto' }}>
              <table style={tblS}>
                <thead>
                  <tr style={{ background: '#f7f6fb' }}>
                    <th style={{ ...thS, textAlign: 'left' }}>근무처</th>
                    <th style={{ ...thS, width: 240 }}>근무 기간</th>
                    <th style={thS}>직위</th>
                    <th style={{ ...thS, textAlign: 'left' }}>업무내용</th>
                  </tr>
                </thead>
                <tbody>
                  {personalInfo.careers?.length > 0 ? personalInfo.careers.map((career, i) => (
                    <tr key={i}>
                      <td style={{ ...tdS, fontWeight: 600, color: '#1f1d2e' }}>{career.organizationName || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }}>{dayjs(career.startDate).format('YYYY년 MM월 DD일')} ~ {career.endDate ? dayjs(career.endDate).format('YYYY년 MM월 DD일') : '재직중'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{career.position || '-'}</td>
                      <td style={tdS}>{career.task || '-'}</td>
                    </tr>
                  )) : emptyRow(4, '이전 근무처 정보가 없습니다.')}
                </tbody>
              </table>
            </div>
          </div>

          {/* IT 교육 */}
          <div style={cardS}>
            {sHead('IT 교육', personalInfo.itTrainings?.length, null)}
            <div style={{ overflowX: 'auto' }}>
              <table style={tblS}>
                <thead>
                  <tr style={{ background: '#f7f6fb' }}>
                    <th style={{ ...thS, textAlign: 'left' }}>교육명</th>
                    <th style={thS}>교육기관</th>
                    <th style={thS}>기간</th>
                    <th style={{ ...thS, textAlign: 'left' }}>교육내용</th>
                  </tr>
                </thead>
                <tbody>
                  {personalInfo.itTrainings?.length > 0 ? personalInfo.itTrainings.map((tr, i) => (
                    <tr key={i}>
                      <td style={{ ...tdS, fontWeight: 600, color: '#1f1d2e' }}>{tr.title || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{tr.institution || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }}>{tr.startDate ? dayjs(tr.startDate).format('YYYY-MM-DD') : '-'} ~ {tr.endDate ? dayjs(tr.endDate).format('YYYY-MM-DD') : '진행 중'}</td>
                      <td style={tdS}>{tr.description || '-'}</td>
                    </tr>
                  )) : emptyRow(4, 'IT 교육 정보가 없습니다.')}
                </tbody>
              </table>
            </div>
          </div>

          {/* 특수기술 */}
          <div style={cardS}>
            {sHead('특수기술', personalInfo.specialSkills?.length, null)}
            <div style={{ overflowX: 'auto' }}>
              <table style={tblS}>
                <thead>
                  <tr style={{ background: '#f7f6fb' }}>
                    <th style={{ ...thS, textAlign: 'left' }}>특수기술명</th>
                    <th style={thS}>숙련도</th>
                  </tr>
                </thead>
                <tbody>
                  {personalInfo.specialSkills?.length > 0 ? personalInfo.specialSkills.map((sk, i) => (
                    <tr key={i}>
                      <td style={{ ...tdS, fontWeight: 600, color: '#1f1d2e' }}>{sk.skillName || '-'}</td>
                      <td style={{ ...tdS, textAlign: 'center' }}>{sk.proficiency || '-'}</td>
                    </tr>
                  )) : emptyRow(2, '특수기술 정보가 없습니다.')}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      {/* 비밀번호 입력 모달 */}
      {showPasswordModal && (
        <div onClick={() => { setShowPasswordModal(false); setPassword(''); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0eff5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#1f1d2e' }}>{passwordModalMode === 'view' ? '연봉 정보 조회' : '연봉 정보 업데이트'}</span>
              <button onClick={() => { setShowPasswordModal(false); setPassword(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#8b8a99', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#5d5b6e' }}>{passwordModalMode === 'view' ? '복호화를 위한 비밀번호를 입력해주세요' : '암호화를 위한 비밀번호를 입력해주세요'}</label>
              <input type="password" style={{ ...inp, width: '100%' }} value={password} onChange={(e) => setPassword(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') handleSalaryPasswordSubmit(); }} placeholder="비밀번호" autoFocus />
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #f0eff5', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={b('outline-secondary')} onClick={() => { setShowPasswordModal(false); setPassword(''); }}>취소</button>
              <button style={b('primary')} onClick={handleSalaryPasswordSubmit}>확인</button>
            </div>
          </div>
        </div>
      )}

      {/* 재직증명서 용도 선택 모달 */}
      {certModalOpen && (
        <div onClick={() => setCertModalOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0eff5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#1f1d2e' }}>증명서 용도 선택</span>
              <button onClick={() => setCertModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#8b8a99', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {['관공서 제출용', '은행 제출용', '기타'].map((opt) => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: '#1f1d2e', cursor: 'pointer' }}>
                  <input type="radio" name="certPurpose" value={opt} checked={certPurposeOption === opt} onChange={() => setCertPurposeOption(opt)} />
                  {opt}
                </label>
              ))}
              {certPurposeOption === '기타' && (
                <input type="text" style={{ ...inp, width: '100%', marginTop: 4 }} placeholder="용도를 입력하세요 (예: 대출 심사용)" value={certPurposeCustom} onChange={(e) => setCertPurposeCustom(e.target.value)} autoFocus />
              )}
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #f0eff5', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={b('outline-secondary')} onClick={() => setCertModalOpen(false)}>취소</button>
              <button style={b('primary')} onClick={handleCareerCertificateDownload}>다운로드</button>
            </div>
          </div>
        </div>
      )}

      <TransferMasterModal
        isOpen={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        organizationCode={currentUser.organizationCode}
        currentUserEmail={user.userEmail}
      />
    </div>
  );
};

export default ViewResumePage;
